import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import {
  from,
  of,
} from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { FormlyFieldConfig } from '@ngx-formly/core';

import {
  DialogFormComponent,
  DialogFormOptions,
} from '../formly/form-dialog.component';
import { formlyInput } from '../formly/formly-helpers';
import {
  Template,
  TemplateQuery,
  TemplateService,
} from '../store';
import { PromptService } from './';
import { CategoryUxService } from './category-ux.service';
import { ToastService } from './toast.service';

@Injectable({ providedIn: 'root' })
export class TemplateUxService {
  constructor(
    private service: TemplateService,
    private query: TemplateQuery,
    private dialog: MatDialog,
    private prompt: PromptService,
    private toast: ToastService,
    private catUx: CategoryUxService
  ) {}

  sync() {
    return this.service.syncCollection({ reset: true });
  }

  getRecord(id: string) {
    return this.query.getEntity(id);
  }

  getAll() {
    return this.query.getAll();
  }

  selectEntity(id: string) {
    return this.query.selectEntity(id);
  }

  selectAll() {
    return this.query.selectAll();
  }

  getEditFields(
    entry: Partial<Template> = {},
    keys: string[] = []
  ): FormlyFieldConfig[] {
    return (
      [
        formlyInput({ key: 'name' }),
        formlyInput({ key: 'template', type: 'textarea' }),
        formlyInput(
          { key: 'category', type: 'select' },
          {
            templateOptions: {
              label: 'Category',
              required: true,
              options: this.catUx.getListOptions(),
            },
          }
        ),
      ] as FormlyFieldConfig[]
    ).filter((thing) =>
      keys && keys.length ? keys.includes(thing.key as string) : true
    );
  }

  edit(template: Template = {}) {
    const options: DialogFormOptions = {
      fields: this.getEditFields(template),
      model: template,
      title: template.id ? 'Edit Template' : 'Add Template',
      remove: template.id ? () => this.remove(template) : undefined,
    };

    const dialog = this.dialog.open(DialogFormComponent, {
      data: options,
    });

    return dialog.afterClosed().pipe(
      switchMap((val) => {
        if (!val) {
          this.toast.open(
            `${template.id ? 'Edit template' : 'Add template'} cancelled`
          );
          return of();
        }
        return from(this.saveRecord(val));
      })
    );
  }

  async saveRecord(model: Template): Promise<string> {
    if (model.id) {
      return this.service.update(model).then(() => {
        this.toast.open(`"${model.name}" Updated`);
        return model.id as string;
      });
    }
    return this.service.add(model).then((val) => {
      this.toast.open(`"${model.name}" Added`);
      return val;
    });
  }

  remove(model: Template) {
    this.prompt.confirm(`Delete template: "${model.name}"`).subscribe((val) => {
      if (!val) {
        return;
      }
      this.dialog.closeAll();
      this.service
        .remove(model.id as string)
        .then(() => this.toast.open(`"${model.name}" removed`));
    });
  }
}
